import { useMedplumProfile } from '@medplum/react-hooks';
import type { Patient, Practitioner, RelatedPerson } from '@medplum/fhirtypes';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Role } from '../pages/users/types/rbac';
import { getUserRoleGroup } from '../pages/users/types/rbac';
import type { UserPermissions } from '../pages/users/types/permissions';
import { defaultRolePermissions } from '../pages/users/types/permissions';
import type { MedplumProfileType, ResolvedPermissions } from '../features/rbac';
import { isMedplumProfileType, resolvePermissionsForRole, resolvePermissionsFromProfile } from '../features/rbac';
import { readStoredActiveRoleSlug, writeStoredActiveRoleSlug } from '../lib/displayUserRole';

// ---------------------------------------------------------------------------
// Context value shape
// ---------------------------------------------------------------------------

export interface PermissionContextValue {
  /** Whether the profile has loaded and permissions are resolved. */
  ready: boolean;
  /** True while the Medplum profile is still loading. */
  loading: boolean;
  /** Non-null when resolution failed (e.g. unrecognized profile type). */
  error: string | null;

  /**
   * Session active role slug (drives nav + UI permission flags).
   * Alias of `activeRole` so existing UI follows the switcher.
   */
  role: Role;
  /** Primary / login-default role (first profile role extension). */
  primaryRole: Role;
  /** Session active role slug. */
  activeRole: Role;
  /** All role slugs assigned to this user (supports multi-role switching). */
  availableRoles: Role[];
  /** Persist a session active role. No-op when the slug is not assigned. */
  setActiveRole: (slug: Role) => void;
  /** 'providers' | 'clients' | 'staff' — drives top-level route bucket. */
  roleGroup: ResolvedPermissions['roleGroup'];
  /** FHIR resource type of the authenticated profile. */
  profileType: MedplumProfileType | null;
  /** Merged UI permissions for the active role (D2). */
  permissions: Partial<UserPermissions>;

  /** True when the user is a patient/client (Patient or RelatedPerson). */
  isClient: boolean;
  /** True when the user is staff (Practitioner with non-client role). */
  isStaff: boolean;
}

const noopSetActiveRole = (_slug: Role): void => undefined;

const DEFAULT_VALUE: PermissionContextValue = {
  ready: false,
  loading: true,
  error: null,
  role: 'client',
  primaryRole: 'client',
  activeRole: 'client',
  availableRoles: ['client'],
  setActiveRole: noopSetActiveRole,
  roleGroup: 'clients',
  profileType: null,
  permissions: defaultRolePermissions['client'],
  isClient: true,
  isStaff: false,
};

const PermissionContext = createContext<PermissionContextValue>(DEFAULT_VALUE);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PermissionProvider({ children }: { children: ReactNode }): JSX.Element {
  const profile = useMedplumProfile() as Patient | Practitioner | RelatedPerson | undefined;
  const [roleEpoch, setRoleEpoch] = useState(0);

  const resolved = useMemo(() => {
    if (!profile) return null;
    const rt = profile.resourceType;
    if (!isMedplumProfileType(rt)) return null;
    return resolvePermissionsFromProfile(profile);
  }, [profile]);

  const setActiveRole = useCallback((slug: Role) => {
    if (!profile || profile.resourceType !== 'Practitioner' || !profile.id) return;
    const available = resolved?.availableRoles ?? [];
    if (!available.includes(slug)) return;
    writeStoredActiveRoleSlug(profile.id, slug);
    setRoleEpoch((n) => n + 1);
  }, [profile, resolved]);

  const value = useMemo<PermissionContextValue>(() => {
    void roleEpoch;
    if (!profile) {
      return DEFAULT_VALUE;
    }

    const rt = profile.resourceType;
    if (!isMedplumProfileType(rt) || !resolved) {
      return {
        ...DEFAULT_VALUE,
        loading: false,
        error: `Unsupported profile type: ${rt}`,
      };
    }

    const primaryRole = resolved.role;
    const availableRoles = resolved.availableRoles;
    const practitionerId = rt === 'Practitioner' ? profile.id : undefined;
    const activeRole = readStoredActiveRoleSlug(practitionerId, availableRoles, primaryRole);
    const permissions = resolvePermissionsForRole(profile, activeRole, primaryRole);
    const isClient = getUserRoleGroup(activeRole) === 'clients';
    return {
      ready: true,
      loading: false,
      error: null,
      role: activeRole,
      primaryRole,
      activeRole,
      availableRoles,
      setActiveRole,
      roleGroup: getUserRoleGroup(activeRole),
      profileType: resolved.profileType,
      permissions,
      isClient,
      isStaff: !isClient,
    };
  }, [profile, resolved, setActiveRole, roleEpoch]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePermissions(): PermissionContextValue {
  return useContext(PermissionContext);
}
